import { buildPosterUrl, isVideo, extraExtensions } from '../../utils/video';
import { buildSource, imageSizes } from '../../utils/productCardImages';
import { applyVideoFetchPrefix } from '../../utils/videoUrlPrefix';

/**
 * Builds a video element for the product card.
 * @param {String} videoSource - URL of the video source
 * @param {String} imageAlt - Alt text for the video element
 * @param {Object} [context]
 * @param {Boolean} [context.hidden] - defer src; lazy observer will load it
 * @param {Boolean} [context.fakePoster] - wrap the video in a div with a fake poster image (default true)
 * @param {String} [context.position] - 'first' | 'second'
 * @param {String} [context.placement] - Cloudinary placement key (e.g. 'tile', 'promo_tile')
 * @returns {HTMLVideoElement|HTMLDivElement} The video element, or a wrapper div containing the fake poster + video when context.fakePoster is true
 */
export const buildVideoElement = (videoSource, imageAlt, context = {}) => {
  const videoElement = document.createElement('video');
  videoElement.ariaLabel = imageAlt;
  videoElement.playsInline = true;
  videoElement.autoplay = true;
  videoElement.muted = true;
  videoElement.loop = true;
  videoElement.setAttribute('preload', 'metadata');

  // Fake poster implementation: it's feature useful for Safari browsers on iOS devices
  // due to Safari not gracefully displaying poster images, it is unreliable and glitches until the video metadata is loaded
  // Apart from that, this introducing a flashing with a gray background and causing a CLS from tiny dimensions.
  // With all that in mind, fake poster is enabled by default for better UX and mitigate CLS.
  const fakePosterFlag = context?.fakePoster ?? true; // Default to true if not provided
  if (!fakePosterFlag) {
    videoElement.poster = buildPosterUrl(videoSource); // Fix gray background but it doesn't fix the glitchy poster display on Safari iOS
  }

  const sourceElement = document.createElement('source');
  sourceElement.type = `video/${isVideo(videoSource).extension}`;

  const placement = context?.placement;
  if (placement) {
    sourceElement.setAttribute('data-placement', placement);
  }

  if (context?.hidden) {
    sourceElement.setAttribute('data-src', videoSource);
  } else {
    sourceElement.src = applyVideoFetchPrefix(videoSource, placement);
  }

  videoElement.appendChild(sourceElement);

  // Add extra source elements for additional video formats to increase the likelihood of compatibility across different browsers and devices
  extraExtensions.map((ext) => {
    const extraSourceElement = document.createElement('source');
    extraSourceElement.type = `video/${ext.replace('.', '')}`;
    const videoUrlWithoutExt = videoSource.split('.').slice(0, -1).join('.');
    const videoSourceWithExt = `${videoUrlWithoutExt}${ext}`;

    if (context?.hidden) {
      extraSourceElement.setAttribute('data-src', videoSourceWithExt);
    } else {
      extraSourceElement.src = applyVideoFetchPrefix(videoSourceWithExt);
    }

    videoElement.appendChild(extraSourceElement);
  });

  if (fakePosterFlag) {
    const videoWrapper = document.createElement('div');
    videoWrapper.style = 'display: flex; width: 100%;';
    videoWrapper.setAttribute('data-video', '');
    const fakePoster = buildFakePoster(videoSource, imageAlt, context?.height);
    videoWrapper.append(fakePoster, videoElement);

    context?.position && videoWrapper.setAttribute(`data-${context?.position}-element`, '');

    // Playloading event + Lazy-load + play while onscreen
    videoElement.addEventListener(
      'playing',
      () => {
        fakePoster.lastChild.classList.add('hidden');
      },
      { once: true },
    );

    return videoWrapper;
  }

  context?.position && videoElement.setAttribute(`data-${context?.position}-element`, '');

  return videoElement;
};

/**
 * Builds a fake poster element for the video.
 * @param {String} videoSource - URL of the video source
 * @param {String} imageAlt - Alt text for the video element
 * @param {Boolean} setHeight - Whether to set the height of the fake poster
 * @returns {HTMLPictureElement}
 */
const buildFakePoster = (videoSource, imageAlt, setHeight = true) => {
  const picture = document.createElement('picture');
  const img = document.createElement('img');
  const posterUrl = buildPosterUrl(videoSource);
  img.src = posterUrl;
  img.alt = imageAlt;
  img.width = 200;
  img.height = 300;

  for (const screenSize of Object.keys(imageSizes)) {
    picture.appendChild(
      buildSource({
        image: posterUrl,
        layout: screenSize,
        setHeight,
      }),
    );
  }

  img.onerror = function () {
    this.style.display = 'none';
    this.closest('div[data-video]').style.background = '#fff';
  };

  picture.appendChild(img);
  return picture;
};
